// ============================================================================
// KLINISOL CRO - Training Certificate Component (COMPACT)
// Single-page layout with QR code, scores, and all FDA compliance features
// ============================================================================

import React from 'react';
import { Award, CheckCircle, Calendar, User, FileText, QrCode, Clock, Target } from 'lucide-react';

const TrainingCertificate = ({ certificateData, onClose, moduleInfo, userInfo }) => {
    const handlePrint = () => {
        window.print();
    };

    const formatDate = (dateString) => {
        if (!dateString || dateString === 'Invalid Date') {
            return 'Date not available';
        }
        
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return 'Date not available';
            }
            
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch (error) {
            console.error('Date formatting error:', error);
            return 'Date not available';
        }
    };

    // Use completed_at from certificateData, fallback to signature_date
    const completionDate = certificateData.completed_at || certificateData.signature_date;
    const expirationDate = certificateData.expires_at;
    const hasQRCode = certificateData.qr_code_data && certificateData.qr_code_data.startsWith('data:image');
    const hasScore = certificateData.score !== null && certificateData.score !== undefined;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-2 bg-gradient-to-br from-[#475569] via-[#E91E63] to-[#475569] rounded-2xl">
                    <div className="bg-white rounded-xl p-6 print:p-8" id="certificate-content">
                        {/* Top Border - Thinner */}
                        <div className="h-2 bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] rounded-t-lg mb-4"></div>

                        {/* Header Section - More Compact */}
                        <div className="text-center mb-4 pb-3">
                            {/* Gradient Background Banner */}
                            <div className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] py-3 px-4 rounded-lg -mx-6 mb-4">
                                <div className="mb-2">
                                    <Award className="w-12 h-12 mx-auto text-white" />
                                </div>
                                <h1 className="text-2xl font-bold mb-1 text-white">
                                    Certificate of Completion
                                </h1>
                                <p className="text-white text-sm font-semibold">KLINISOL Industry Standard Training</p>
                            </div>
                        </div>

                        {/* Certificate Body - Compact */}
                        <div className="mb-4 text-center">
                            <p className="text-gray-700 text-sm mb-2">This certifies that</p>
                            <h2 className="text-2xl font-bold text-[#475569] mb-2">
                                {userInfo.name}
                            </h2>
                            <p className="text-gray-700 text-sm mb-1">has successfully completed</p>
                            <h3 className="text-xl font-semibold bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] bg-clip-text text-transparent mb-2">
                                {moduleInfo.name}
                            </h3>
                            <p className="text-gray-600 text-sm">
                                Module Code: <span className="font-semibold text-[#475569]">{moduleInfo.code}</span>
                            </p>
                        </div>

                        {/* Two Column Layout: Details + QR Code */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            {/* Left: Certificate Details (2 columns) */}
                            <div className="md:col-span-2">
                                <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-lg border-2 border-gray-200">
                                    <div className="grid grid-cols-2 gap-3 text-sm">
                                        {/* Completion Date */}
                                        <div className="flex items-start gap-2">
                                            <Calendar className="w-4 h-4 text-[#E91E63] mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="text-xs text-gray-600">Completion Date</p>
                                                <p className="font-semibold text-[#475569] text-xs">
                                                    {formatDate(completionDate)}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Expiration Date */}
                                        {expirationDate && (
                                            <div className="flex items-start gap-2">
                                                <Clock className="w-4 h-4 text-[#E91E63] mt-0.5 flex-shrink-0" />
                                                <div>
                                                    <p className="text-xs text-gray-600">Valid Until</p>
                                                    <p className="font-semibold text-[#475569] text-xs">
                                                        {formatDate(expirationDate)}
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        {/* Signed By */}
                                        <div className="flex items-start gap-2">
                                            <User className="w-4 h-4 text-[#E91E63] mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="text-xs text-gray-600">Signed By</p>
                                                <p className="font-semibold text-[#475569] text-xs">
                                                    {certificateData.signature_full_name || userInfo.name}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Quiz Score */}
                                        {hasScore && (
                                            <div className="flex items-start gap-2">
                                                <Target className="w-4 h-4 text-[#E91E63] mt-0.5 flex-shrink-0" />
                                                <div>
                                                    <p className="text-xs text-gray-600">Quiz Score</p>
                                                    <p className="font-semibold text-[#475569] text-xs">
                                                        {certificateData.score}%
                                                        {certificateData.points_earned && certificateData.total_points && (
                                                            <span className="text-xs text-gray-600 ml-1">
                                                                ({certificateData.points_earned}/{certificateData.total_points})
                                                            </span>
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        {/* Certificate ID */}
                                        <div className="flex items-start gap-2 col-span-2">
                                            <FileText className="w-4 h-4 text-[#E91E63] mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="text-xs text-gray-600">Certificate ID</p>
                                                <p className="font-mono text-xs font-semibold text-[#475569]">
                                                    {certificateData.certificate_number}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Verification Status */}
                                        <div className="flex items-start gap-2 col-span-2">
                                            <CheckCircle className="w-4 h-4 text-[#E91E63] mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="text-xs text-gray-600">Verification Status</p>
                                                <p className="font-semibold text-green-600 text-xs">FDA 21 CFR Part 11 Compliant</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right: QR Code (1 column) */}
                            {hasQRCode && (
                                <div className="flex justify-center items-center">
                                    <div className="bg-white p-3 rounded-lg border-4 border-[#475569] shadow-md">
                                        <img 
                                            src={certificateData.qr_code_data} 
                                            alt="Certificate Verification QR Code"
                                            className="w-28 h-28 print:w-24 print:h-24"
                                        />
                                        <p className="text-[10px] text-center text-gray-600 font-semibold mt-2 flex items-center gap-1 justify-center">
                                            <QrCode className="w-3 h-3 text-[#E91E63]" />
                                            Scan to Verify
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Electronic Signature Compliance - More Compact */}
                        <div className="bg-gradient-to-br from-[#475569]/5 to-[#E91E63]/5 border-l-4 border-[#E91E63] p-3 rounded-r-lg mb-4">
                            <p className="text-xs text-gray-700 leading-relaxed">
                                <strong className="text-[#475569]">Electronic Signature:</strong> This document has been electronically signed 
                                by {certificateData.signature_full_name || userInfo.name} on {formatDate(certificateData.signature_date)} 
                                in compliance with 21 CFR Part 11 requirements. This certificate includes a cryptographic hash for tamper detection.
                            </p>
                        </div>

                        {/* Footer - Compact */}
                        <div className="text-center pt-3">
                            {/* Gradient Background Banner */}
                            <div className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] py-3 px-4 rounded-lg -mx-6">
                                <h3 className="text-xl font-bold text-white mb-1">
                                    KLIN<span className="text-white">i</span>SOL
                                </h3>
                                <p className="text-xs text-white italic mb-1">Advancing Investigator-Initiated Research</p>
                                <p className="text-[10px] text-white opacity-90">
                                    600 Broadway Ste 200 #2189, Albany, NY 12207 • info@klinisol.com • klinisol.com
                                </p>
                            </div>
                        </div>

                        {/* Bottom Border - Thinner */}
                        <div className="h-2 bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] rounded-b-lg mt-4"></div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="px-8 pb-8 flex gap-4 justify-center print:hidden mt-6 flex-wrap">
                    <button
                        onClick={handlePrint}
                        className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white font-semibold rounded-lg hover:from-[#E91E63] hover:via-[#475569] hover:to-[#E91E63] transition-all shadow-lg transform hover:scale-105"
                    >
                        <FileText className="w-5 h-5" />
                        Print Certificate
                    </button>
                    
                    {certificateData.verification_url && (
                        <a
                            href={certificateData.verification_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg transform hover:scale-105"
                        >
                            <QrCode className="w-5 h-5" />
                            Verify Certificate
                        </a>
                    )}
                    
                    <button
                        onClick={onClose}
                        className="flex items-center gap-2 px-6 py-3 bg-white text-[#475569] border-2 border-[#475569] font-semibold rounded-lg hover:bg-gradient-to-r hover:from-[#475569] hover:to-[#E91E63] hover:text-white hover:border-transparent transition-all transform hover:scale-105"
                    >
                        Close
                    </button>
                </div>
            </div>

            {/* Enhanced Print Styles */}
            <style>{`
                @media print {
                    body * {
                        visibility: hidden;
                    }
                    #certificate-content,
                    #certificate-content * {
                        visibility: visible;
                    }
                    #certificate-content {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                        page-break-after: avoid;
                        page-break-inside: avoid;
                    }
                    @page {
                        size: letter portrait;
                        margin: 0.5in;
                    }
                    /* Ensure QR code prints properly */
                    img {
                        print-color-adjust: exact;
                        -webkit-print-color-adjust: exact;
                        page-break-inside: avoid;
                    }
                    /* Ensure gradients print properly */
                    .bg-gradient-to-r,
                    .bg-gradient-to-br,
                    .bg-clip-text {
                        print-color-adjust: exact;
                        -webkit-print-color-adjust: exact;
                    }
                    /* Prevent page breaks */
                    .grid,
                    .flex {
                        page-break-inside: avoid;
                    }
                }
            `}</style>
        </div>
    );
};

export default TrainingCertificate;